/**
 * ChangeRelationshipFlow
 * ======================
 *
 * Single-screen flow for updating an informant's relationship to the patient.
 *
 * Displays the current relationship, accepts a new value, validates it
 * client-side (non-empty, different from current), and submits. On success,
 * a confirmation screen is shown.
 *
 * Input sanitization uses sanitizeNameInput — the same character filter
 * applied to the relationship field in AddInformantFlow, ReplaceInformantFlow,
 * SplitInformantFlow, and InformantSetupForm. This restricts input to Unicode
 * letters, spaces, hyphens, and apostrophes only.
 *
 * Owns all form state internally so the parent doesn't need to track
 * relationship inputs, validation errors, or success flags.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import WarningPanel from './WarningPanel';
import ConfirmationScreen from './ConfirmationScreen';
import ButtonSpinner from './ButtonSpinner';
import InformantFormInput from './InformantFormInput';
import { validateRelationship } from '../../utils/informantValidation';

/**
 * @param {Object}   props
 * @param {Object}   props.informant            – The informant whose relationship is changing
 * @param {Function} props.onChangeRelationship  – (informantId, newRelationship) => Promise
 * @param {Function} props.onBack                – Navigate back to the detail screen
 * @param {boolean}  props.actionLoading         – Whether the backend call is in progress
 * @param {string}   props.actionError           – Error message from the backend
 * @param {Function} props.clearActionState      – Reset actionError/actionSuccess
 * @param {Function} props.onFinish              – Called when user clicks "Finish" on success screen
 * @param {boolean}  props.isFinishing           – Show spinner on the "Finish" button
 * @param {Object}   props.terminology           – { singular, lowerSingular }
 * @param {Function} props.sanitizeNameInput     – Input sanitizer for name-like fields
 * @param {string}   [props.callerType]          – 'patient' | 'provider'; controls whether
 *                                                  the report re-finalisation warning is shown
 */
export default function ChangeRelationshipFlow({
  informant,
  onChangeRelationship,
  onBack,
  actionLoading = false,
  actionError = null,
  clearActionState,
  onFinish,
  isFinishing = false,
  terminology = { singular: 'Informant', lowerSingular: 'informant' },
  sanitizeNameInput,
  callerType = 'patient',
}) {
  const [newRelationship, setNewRelationship] = useState('');
  const [relationshipError, setRelationshipError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handleConfirm = useCallback(async () => {
    const trimmed = newRelationship.trim();

    // Validate non-empty (same check as validateNewInformant uses)
    const formatErr = validateRelationship(trimmed);
    if (formatErr) { setRelationshipError(formatErr); return; }

    // Validate different from current (case-insensitive since backend normalises casing)
    if (trimmed.toLowerCase() === (informant?.relationship || '').toLowerCase()) {
      setRelationshipError('New relationship must be different from the current one.');
      return;
    }

    setRelationshipError('');
    try {
      await onChangeRelationship(informant.id, trimmed);
      if (mountedRef.current) setIsSuccess(true);
    } catch {
      // actionError handled by the hook
    }
  }, [onChangeRelationship, informant, newRelationship]);

  // Derived: is the current input valid and different from the existing value?
  const isValid =
    !validateRelationship(newRelationship.trim()) &&
    newRelationship.trim().toLowerCase() !== (informant?.relationship || '').toLowerCase();

  if (isSuccess) {
    return (
      <ConfirmationScreen
        headline="Relationship updated successfully!"
        detail={`This ${terminology.lowerSingular}'s relationship is now recorded as "${newRelationship.trim()}".`}
        onFinish={onFinish}
        isLoading={isFinishing}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Change Relationship
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          {callerType === 'patient'
            ? <>Update your relationship to{' '}
                <span className="font-medium">{informant.firstName} {informant.lastName}</span>.
              </>
            : <>Update the relationship for{' '}
                <span className="font-medium">{informant.firstName} {informant.lastName}</span>.
              </>
          }
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
        <span className="text-xs text-gray-600 uppercase tracking-wider font-semibold">
          Current relationship
        </span>
        <p className="text-xs font-medium text-gray-800 mt-0.5">
          {informant.relationship || 'Not set'}
        </p>
      </div>

      <InformantFormInput
        label={callerType === 'patient' ? 'New relationship' : 'New relationship to patient'}
        type="text"
        value={newRelationship}
        onChange={(e) => {
          setNewRelationship(sanitizeNameInput?.(e.target.value) ?? e.target.value);
          if (relationshipError) setRelationshipError('');
        }}
        error={relationshipError}
        required
        autoComplete="off"
      />

      {isValid && callerType === 'provider' && (
        <WarningPanel variant="warning">
          <ul className="list-disc pl-4 space-y-1">
            <li><strong>If any reports have already been finalised, they will need to be re-finalised</strong> to reflect the updated relationship.</li>
          </ul>
        </WarningPanel>
      )}

      {actionError && <WarningPanel variant="error">{actionError}</WarningPanel>}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={() => { clearActionState?.(); onBack(); }}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={actionLoading || !isValid}
          className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
            actionLoading || !isValid
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-violet-600 hover:bg-violet-700'
          }`}
        >
          {actionLoading ? (
            <span className="inline-flex items-center gap-2">
              <ButtonSpinner />
              Updating…
            </span>
          ) : 'Confirm'}
        </button>
      </div>
    </div>
  );
}
